/**
 * Analysis Engine Service
 * Transforms questionnaire and image metadata into a structured Skin Report
 * Context-aware for South African climate conditions
 */

import { UserProfile } from '@/types';

// Product catalog type definition
export interface Product {
  sku: string;
  name: string;
  category: 'cleanser' | 'toner' | 'serum' | 'moisturizer' | 'sunscreen' | 'mask' | 'treatment';
  heroIngredient: string;
  benefits: string[];
  skinTypes: string[];
  concerns: string[];
  amUse: boolean;
  pmUse: boolean;
  weeklyUse?: number; // times per week if applicable
}

// Analysis result types
export interface SkinFinding {
  label: string;
  value: string;
  confidence: number;
  explanation: string;
  severity?: 'low' | 'moderate' | 'high';
}

export interface RegimenStep {
  step: number;
  category: string;
  productSku: string;
  productName: string;
  instruction: string;
  heroIngredient?: string;
}

export interface RegimenPlan {
  am: RegimenStep[];
  pm: RegimenStep[];
  weekly?: RegimenStep[];
}

export interface SkinReport {
  findings: SkinFinding[];
  regimenPlan: RegimenPlan;
  cautions: string[];
  heroBotanical: string;
  heroBotanicalOrigin: string;
  heroBotanicalBenefits: string[];
  nextBestAction: string;
  skinProfile: {
    type: string;
    concerns: string[];
    sensitivity: string;
    environment: string;
  };
  climateContext: string;
}

// South African climate context data
const SA_CLIMATE_CONTEXTS: Record<string, {
  name: string;
  characteristics: string[];
  skinChallenges: string[];
  recommendations: string[];
}> = {
  'highveld': {
    name: 'Highveld (Johannesburg/Pretoria)',
    characteristics: ['High altitude', 'Low humidity', 'Intense UV exposure', 'Dry winters'],
    skinChallenges: ['Dehydration', 'UV damage', 'Trans-epidermal water loss', 'Premature aging'],
    recommendations: ['Layered hydration', 'SPF 50+ daily', 'Humectant-rich products', 'Barrier repair']
  },
  'coastal': {
    name: 'Coastal (Cape Town/Durban)',
    characteristics: ['Higher humidity', 'Sea salt exposure', 'Variable weather', 'Wind exposure'],
    skinChallenges: ['Clogged pores', 'Excess oil', 'Environmental pollution', 'Sensitivity'],
    recommendations: ['Gentle cleansing', 'Antioxidant protection', 'Lightweight hydration', 'Pollution defense']
  },
  'karoo': {
    name: 'Karoo/Arid Regions',
    characteristics: ['Extreme dryness', 'High temperatures', 'Minimal rainfall', 'Harsh winds'],
    skinChallenges: ['Severe dehydration', 'Flaking', 'Sensitivity', 'Barrier dysfunction'],
    recommendations: ['Rich emollients', 'Occlusive layering', 'Gentle exfoliation', 'Intensive hydration']
  },
  'subtropical': {
    name: 'Subtropical (Lowveld/KZN)',
    characteristics: ['High humidity', 'Heat', 'Monsoon seasons', 'Intense sun'],
    skinChallenges: ['Excess sebum', 'Heat rash', 'Hyperpigmentation', 'Fungal acne'],
    recommendations: ['Lightweight formulas', 'Oil control', 'Brightening ingredients', 'Breathable textures']
  }
};

// Hero botanicals database
const HERO_BOTANICALS: Record<string, {
  name: string;
  origin: string;
  benefits: string[];
  bestFor: string[];
}> = {
  'kalahari_melon': {
    name: 'Kalahari Melon Oil',
    origin: 'The Kalahari Desert',
    benefits: [
      'Rich in linoleic acid (70%) for barrier repair',
      'Lightweight, non-comedogenic hydration',
      'Natural vitamin E content',
      'Balances oil production'
    ],
    bestFor: ['oily', 'combination', 'acne-prone', 'dehydrated']
  },
  'baobab': {
    name: 'Baobab Oil',
    origin: 'The Tree of Life, Southern Africa',
    benefits: [
      'Exceptional moisturizing for dry climates',
      'High in vitamins A, D, E, and F',
      'Improves skin elasticity',
      'Anti-aging properties'
    ],
    bestFor: ['dry', 'mature', 'aging', 'dehydrated']
  },
  'mongongo': {
    name: 'Mongongo Nut Oil',
    origin: 'The Zambezi Valley',
    benefits: [
      'Natural UV protection properties',
      'Rich in eleostearic acid',
      'Deep nourishment and repair',
      'Protects against environmental stress'
    ],
    bestFor: ['sun-damaged', 'environmental-stress', 'dry', 'mature']
  },
  'marula': {
    name: 'Marula Oil',
    origin: 'Sub-Saharan Africa',
    benefits: [
      'High antioxidant content (60% more than argan)',
      'Fast absorption, non-greasy',
      'Reduces redness and inflammation',
      'Promotes radiant, glowing skin'
    ],
    bestFor: ['sensitive', 'dull', 'normal', 'combination']
  },
  'rooibos': {
    name: 'Rooibos Extract',
    origin: 'Cederberg Mountains, Western Cape',
    benefits: [
      'Powerful anti-inflammatory',
      'Rich in antioxidants (aspalathin)',
      'Soothes sensitive and reactive skin',
      'Anti-aging and protective'
    ],
    bestFor: ['sensitive', 'reactive', 'rosacea-prone', 'inflamed']
  },
  'honeybush': {
    name: 'Honeybush Extract',
    origin: 'Eastern Cape',
    benefits: [
      'Natural antimicrobial properties',
      'Promotes skin healing',
      'Gentle on compromised skin barriers',
      'Rich in minerals and flavonoids'
    ],
    bestFor: ['acne-prone', 'compromised-barrier', 'healing-needed']
  }
};

// Sample product catalog (would be fetched from backend in production)
const PRODUCT_CATALOG: Product[] = [
  {
    sku: 'MAR-001',
    name: 'Baobab Nourishing Cleanser',
    category: 'cleanser',
    heroIngredient: 'baobab',
    benefits: ['Gentle cleansing', 'Maintains moisture barrier', 'Non-stripping'],
    skinTypes: ['dry', 'mature', 'normal', 'sensitive'],
    concerns: ['dehydration', 'aging'],
    amUse: true,
    pmUse: true
  },
  {
    sku: 'MAR-002',
    name: 'Kalahari Balancing Cleanser',
    category: 'cleanser',
    heroIngredient: 'kalahari_melon',
    benefits: ['Oil control', 'Gentle exfoliation', 'Pore refinement'],
    skinTypes: ['oily', 'combination', 'acne-prone'],
    concerns: ['excess oil', 'congested pores'],
    amUse: true,
    pmUse: true
  },
  {
    sku: 'MAR-003',
    name: 'Rooibos Soothing Toner',
    category: 'toner',
    heroIngredient: 'rooibos',
    benefits: ['Calms inflammation', 'Balances pH', 'Preps skin for treatment'],
    skinTypes: ['sensitive', 'reactive', 'all'],
    concerns: ['redness', 'sensitivity', 'inflammation'],
    amUse: true,
    pmUse: true
  },
  {
    sku: 'MAR-004',
    name: 'Kalahari Light Hydration Serum',
    category: 'serum',
    heroIngredient: 'kalahari_melon',
    benefits: ['Lightweight hydration', 'Barrier repair', 'Non-comedogenic'],
    skinTypes: ['oily', 'combination', 'acne-prone'],
    concerns: ['dehydration', 'barrier repair'],
    amUse: true,
    pmUse: true
  },
  {
    sku: 'MAR-005',
    name: 'Baobab Deep Moisture Serum',
    category: 'serum',
    heroIngredient: 'baobab',
    benefits: ['Intensive hydration', 'Anti-aging', 'Elasticity improvement'],
    skinTypes: ['dry', 'mature', 'dehydrated'],
    concerns: ['aging', 'dehydration', 'loss of firmness'],
    amUse: true,
    pmUse: true
  },
  {
    sku: 'MAR-006',
    name: 'Mongongo Protective Day Cream SPF 30',
    category: 'moisturizer',
    heroIngredient: 'mongongo',
    benefits: ['UV protection', 'Environmental defense', 'Daily hydration'],
    skinTypes: ['all'],
    concerns: ['sun damage', 'environmental stress'],
    amUse: true,
    pmUse: false
  },
  {
    sku: 'MAR-007',
    name: 'Marula Radiance Night Cream',
    category: 'moisturizer',
    heroIngredient: 'marula',
    benefits: ['Overnight repair', 'Antioxidant boost', 'Radiance enhancement'],
    skinTypes: ['normal', 'dry', 'dull'],
    concerns: ['dullness', 'environmental damage'],
    amUse: false,
    pmUse: true
  },
  {
    sku: 'MAR-008',
    name: 'Honeybush Clarifying Treatment',
    category: 'treatment',
    heroIngredient: 'honeybush',
    benefits: ['Targets blemishes', 'Promotes healing', 'Antimicrobial'],
    skinTypes: ['acne-prone', 'oily', 'combination'],
    concerns: ['acne', 'blemishes', 'congestion'],
    amUse: false,
    pmUse: true,
    weeklyUse: 3
  },
  {
    sku: 'MAR-009',
    name: 'Baobab Intense Hydration Mask',
    category: 'mask',
    heroIngredient: 'baobab',
    benefits: ['Deep hydration boost', 'Barrier repair', 'Plumping effect'],
    skinTypes: ['dry', 'dehydrated', 'mature'],
    concerns: ['dehydration', 'dullness'],
    amUse: false,
    pmUse: false,
    weeklyUse: 2
  },
  {
    sku: 'MAR-010',
    name: 'Rooibos Calming Mask',
    category: 'mask',
    heroIngredient: 'rooibos',
    benefits: ['Reduces redness', 'Soothes irritation', 'Strengthens barrier'],
    skinTypes: ['sensitive', 'reactive', 'inflamed'],
    concerns: ['redness', 'sensitivity', 'inflammation'],
    amUse: false,
    pmUse: false,
    weeklyUse: 2
  }
];

/**
 * Detect climate context from location/environment
 */
function detectClimateContext(environment: string): typeof SA_CLIMATE_CONTEXTS[keyof typeof SA_CLIMATE_CONTEXTS] {
  const env = environment.toLowerCase();
  
  if (env.includes('johannesburg') || env.includes('pretoria') || env.includes('gauteng') || env.includes('highveld')) {
    return SA_CLIMATE_CONTEXTS.highveld;
  }
  if (env.includes('cape') || env.includes('durban') || env.includes('coastal') || env.includes('beach')) {
    return SA_CLIMATE_CONTEXTS.coastal;
  }
  if (env.includes('karoo') || env.includes('desert') || env.includes('arid') || env.includes('namibia')) {
    return SA_CLIMATE_CONTEXTS.karoo;
  }
  if (env.includes('mpumalanga') || env.includes('lowveld') || env.includes('kzn') || env.includes('subtropical')) {
    return SA_CLIMATE_CONTEXTS.subtropical;
  }
  
  // Default to highveld for South African context
  return SA_CLIMATE_CONTEXTS.highveld;
}

/**
 * Select hero botanical based on skin profile
 */
function selectHeroBotanical(
  skinType: string,
  concerns: string[],
  climate: string
): { key: string; info: typeof HERO_BOTANICALS[keyof typeof HERO_BOTANICALS] } {
  const type = skinType.toLowerCase();
  const concernList = concerns.map(c => c.toLowerCase());
  
  // Priority matching based on primary concern
  if (concernList.some(c => c.includes('acne') || c.includes('breakout') || c.includes('oil'))) {
    return { key: 'kalahari_melon', info: HERO_BOTANICALS.kalahari_melon };
  }
  
  if (concernList.some(c => c.includes('sensitive') || c.includes('red') || c.includes('inflamed'))) {
    return { key: 'rooibos', info: HERO_BOTANICALS.rooibos };
  }
  
  if (concernList.some(c => c.includes('dry') || c.includes('dehydrated') || c.includes('flaking'))) {
    return { key: 'baobab', info: HERO_BOTANICALS.baobab };
  }
  
  if (concernList.some(c => c.includes('sun') || c.includes('damage') || c.includes('environmental'))) {
    return { key: 'mongongo', info: HERO_BOTANICALS.mongongo };
  }
  
  if (concernList.some(c => c.includes('dull') || c.includes('radiance') || c.includes('glow'))) {
    return { key: 'marula', info: HERO_BOTANICALS.marula };
  }
  
  if (concernList.some(c => c.includes('heal') || c.includes('repair') || c.includes('barrier'))) {
    return { key: 'honeybush', info: HERO_BOTANICALS.honeybush };
  }
  
  // Fall back to skin type matching
  if (type.includes('oily') || type.includes('combination')) {
    return { key: 'kalahari_melon', info: HERO_BOTANICALS.kalahari_melon };
  }
  
  if (type.includes('dry') || type.includes('mature')) {
    return { key: 'baobab', info: HERO_BOTANICALS.baobab };
  }
  
  if (type.includes('sensitive')) {
    return { key: 'rooibos', info: HERO_BOTANICALS.rooibos };
  }
  
  // Default
  return { key: 'marula', info: HERO_BOTANICALS.marula };
}

/**
 * Generate skin findings based on questionnaire and profile
 */
function generateFindings(
  profile: Partial<UserProfile>,
  questionnaire: Record<string, any>,
  climate: typeof SA_CLIMATE_CONTEXTS[keyof typeof SA_CLIMATE_CONTEXTS]
): SkinFinding[] {
  const findings: SkinFinding[] = [];
  
  // Skin type finding
  findings.push({
    label: 'Skin Type',
    value: profile.skinType || questionnaire.skinType || 'Combination',
    confidence: 0.85,
    explanation: `Based on your self-assessment and reported characteristics.`
  });
  
  // Hydration analysis
  const hydrationIndicators = [
    questionnaire.tightnessAfterCleansing,
    profile.environment,
    climate.characteristics.includes('Low humidity')
  ];
  const dehydrationRisk = hydrationIndicators.filter(Boolean).length;
  
  if (dehydrationRisk >= 2) {
    findings.push({
      label: 'Hydration Level',
      value: 'Dehydrated',
      confidence: 0.88,
      explanation: `Based on reported tightness and ${climate.name} conditions with low humidity. Your skin is losing moisture to the environment.`,
      severity: 'moderate'
    });
  } else if (dehydrationRisk === 1) {
    findings.push({
      label: 'Hydration Level',
      value: 'Mildly Dehydrated',
      confidence: 0.75,
      explanation: 'Some signs of moisture loss detected. Preventive hydration recommended.',
      severity: 'low'
    });
  } else {
    findings.push({
      label: 'Hydration Level',
      value: 'Adequate',
      confidence: 0.80,
      explanation: 'Your skin appears to be maintaining good moisture levels.'
    });
  }
  
  // Sensitivity analysis
  if (questionnaire.sensitivity === 'high' || questionnaire.triggers?.length > 2) {
    findings.push({
      label: 'Sensitivity Level',
      value: 'High Reactivity',
      confidence: 0.90,
      explanation: `Multiple triggers identified: ${questionnaire.triggers?.join(', ') || 'environmental factors'}. Gentle, fragrance-free formulations recommended.`,
      severity: 'high'
    });
  } else if (questionnaire.sensitivity === 'moderate') {
    findings.push({
      label: 'Sensitivity Level',
      value: 'Moderate Sensitivity',
      confidence: 0.82,
      explanation: 'Some reactivity to products or environment. Patch testing recommended for new products.',
      severity: 'moderate'
    });
  }
  
  // UV/Environmental stress
  if (climate.characteristics.includes('Intense UV exposure')) {
    findings.push({
      label: 'Environmental Stress',
      value: 'High UV Exposure Risk',
      confidence: 0.95,
      explanation: `${climate.name} conditions expose skin to intense UV radiation. Daily SPF 50+ essential.`,
      severity: 'high'
    });
  }
  
  // Concern-based findings
  if (questionnaire.concerns?.includes('acne') || questionnaire.concerns?.includes('breakouts')) {
    findings.push({
      label: 'Primary Concern',
      value: 'Acne/Breakouts',
      confidence: 0.85,
      explanation: 'Active breakouts or congestion detected. Non-comedogenic formulations and gentle exfoliation recommended.',
      severity: 'moderate'
    });
  }
  
  if (questionnaire.concerns?.includes('aging') || questionnaire.concerns?.includes('wrinkles')) {
    findings.push({
      label: 'Primary Concern',
      value: 'Aging Prevention',
      confidence: 0.80,
      explanation: 'Focus on antioxidant protection, hydration, and gentle retinoid introduction.',
      severity: 'low'
    });
  }
  
  // Barrier health
  if (questionnaire.currentRoutine === 'minimal' || questionnaire.currentRoutine === 'none') {
    findings.push({
      label: 'Barrier Function',
      value: 'Needs Support',
      confidence: 0.70,
      explanation: 'Basic routine recommended to establish healthy skin barrier function.',
      severity: 'low'
    });
  }
  
  return findings;
}

/**
 * Build personalized regimen plan
 */
function buildRegimenPlan(
  skinType: string,
  concerns: string[],
  heroBotanical: string,
  sensitivity: string
): RegimenPlan {
  const am: RegimenStep[] = [];
  const pm: RegimenStep[] = [];
  const weekly: RegimenStep[] = [];
  
  let stepCount = 1;
  
  // AM Routine
  // Step 1: Cleanse
  const cleanser = PRODUCT_CATALOG.find(p => 
    p.category === 'cleanser' && 
    p.skinTypes.some(st => skinType.toLowerCase().includes(st) || st === 'all')
  ) || PRODUCT_CATALOG[0];
  
  am.push({
    step: stepCount++,
    category: 'Cleanse',
    productSku: cleanser.sku,
    productName: cleanser.name,
    instruction: 'Massage onto damp skin in circular motions for 60 seconds. Rinse with lukewarm water.',
    heroIngredient: HERO_BOTANICALS[cleanser.heroIngredient as keyof typeof HERO_BOTANICALS]?.name
  });
  
  // Step 2: Tone (if sensitive)
  if (sensitivity === 'high' || sensitivity === 'moderate') {
    const toner = PRODUCT_CATALOG.find(p => p.sku === 'MAR-003');
    if (toner) {
      am.push({
        step: stepCount++,
        category: 'Tone',
        productSku: toner.sku,
        productName: toner.name,
        instruction: 'Apply to cotton pad and sweep across face, or pat directly onto skin.',
        heroIngredient: HERO_BOTANICALS.rooibos.name
      });
    }
  }
  
  // Step 3: Treat (Serum)
  const serum = PRODUCT_CATALOG.find(p => 
    p.category === 'serum' && 
    p.heroIngredient === heroBotanical
  ) || PRODUCT_CATALOG.find(p => p.category === 'serum');
  
  if (serum) {
    am.push({
      step: stepCount++,
      category: 'Treat',
      productSku: serum.sku,
      productName: serum.name,
      instruction: 'Apply 2-3 drops to face and neck. Press gently into skin.',
      heroIngredient: HERO_BOTANICALS[serum.heroIngredient as keyof typeof HERO_BOTANICALS]?.name
    });
  }
  
  // Step 4: Moisturize + Protect
  const dayMoisturizer = PRODUCT_CATALOG.find(p => p.sku === 'MAR-006');
  if (dayMoisturizer) {
    am.push({
      step: stepCount++,
      category: 'Protect',
      productSku: dayMoisturizer.sku,
      productName: dayMoisturizer.name,
      instruction: 'Apply generously as final step. Reapply every 2 hours when outdoors.',
      heroIngredient: HERO_BOTANICALS.mongongo.name
    });
  }
  
  // PM Routine
  stepCount = 1;
  
  // Step 1: Cleanse (double cleanse if wearing SPF/makeup)
  pm.push({
    step: stepCount++,
    category: 'Cleanse',
    productSku: cleanser.sku,
    productName: cleanser.name,
    instruction: 'Double cleanse to remove SPF and impurities. First cleanse removes surface debris, second cleanses the skin.',
    heroIngredient: HERO_BOTANICALS[cleanser.heroIngredient as keyof typeof HERO_BOTANICALS]?.name
  });
  
  // Step 2: Treat (can use stronger actives at night)
  if (concerns.some(c => c.includes('acne'))) {
    const treatment = PRODUCT_CATALOG.find(p => p.sku === 'MAR-008');
    if (treatment) {
      pm.push({
        step: stepCount++,
        category: 'Treat',
        productSku: treatment.sku,
        productName: treatment.name,
        instruction: 'Apply to affected areas only. Do not use with other active treatments on same night.',
        heroIngredient: HERO_BOTANICALS.honeybush.name
      });
    }
  }
  
  // Step 3: Serum (if not using treatment, or additional hydration)
  if (serum && !concerns.some(c => c.includes('acne'))) {
    pm.push({
      step: stepCount++,
      category: 'Nourish',
      productSku: serum.sku,
      productName: serum.name,
      instruction: 'Apply 2-3 drops. Layer under moisturizer for enhanced hydration.',
      heroIngredient: HERO_BOTANICALS[serum.heroIngredient as keyof typeof HERO_BOTANICALS]?.name
    });
  }
  
  // Step 4: Night Cream
  const nightCream = PRODUCT_CATALOG.find(p => p.sku === 'MAR-007');
  if (nightCream) {
    pm.push({
      step: stepCount++,
      category: 'Repair',
      productSku: nightCream.sku,
      productName: nightCream.name,
      instruction: 'Apply generously as final step. Allow to absorb before bed.',
      heroIngredient: HERO_BOTANICALS.marula.name
    });
  }
  
  // Weekly Treatments
  if (concerns.some(c => c.includes('dry') || c.includes('dehydrated'))) {
    const mask = PRODUCT_CATALOG.find(p => p.sku === 'MAR-009');
    if (mask) {
      weekly.push({
        step: 1,
        category: 'Mask',
        productSku: mask.sku,
        productName: mask.name,
        instruction: 'Apply to clean skin, leave for 15-20 minutes. Use 2x weekly for intensive hydration.',
        heroIngredient: HERO_BOTANICALS.baobab.name
      });
    }
  }
  
  if (sensitivity === 'high' || sensitivity === 'moderate') {
    const calmingMask = PRODUCT_CATALOG.find(p => p.sku === 'MAR-010');
    if (calmingMask) {
      weekly.push({
        step: weekly.length + 1,
        category: 'Mask',
        productSku: calmingMask.sku,
        productName: calmingMask.name,
        instruction: 'Use on irritated skin as needed. Leave for 10-15 minutes.',
        heroIngredient: HERO_BOTANICALS.rooibos.name
      });
    }
  }
  
  return { am, pm, weekly: weekly.length > 0 ? weekly : undefined };
}

/**
 * Generate product usage cautions
 */
function generateCautions(
  regimenPlan: RegimenPlan,
  sensitivity: string,
  concerns: string[]
): string[] {
  const cautions: string[] = [];
  
  // Active ingredient conflicts
  const hasVitaminC = false; // Would check actual products
  const hasRetinol = false;
  const hasAcid = false;
  
  if (sensitivity === 'high') {
    cautions.push('Introduce one new product at a time, waiting 3-4 days between additions.');
    cautions.push('Always patch test new products on inner arm 24 hours before full face application.');
  }
  
  if (concerns.some(c => c.includes('acne'))) {
    cautions.push('Do not use Honeybush Clarifying Treatment on the same night as other active treatments.');
    cautions.push('If using prescription acne medication, consult your dermatologist before adding new actives.');
  }
  
  // AM/PM specific cautions
  const amActives = regimenPlan.am.filter(s => s.category === 'Treat');
  if (amActives.length > 1) {
    cautions.push('Space active treatments 5-10 minutes apart to allow absorption.');
  }
  
  // Sun protection reminder
  cautions.push('Daily SPF application is essential, even on cloudy days and indoors near windows.');
  
  // Pregnancy/breastfeeding note
  cautions.push('If pregnant or breastfeeding, consult your healthcare provider before using products with essential oils or active ingredients.');
  
  return cautions;
}

/**
 * Determine next best action
 */
function determineNextBestAction(
  findings: SkinFinding[],
  concerns: string[],
  currentRoutine: string
): string {
  if (currentRoutine === 'none' || currentRoutine === 'minimal') {
    return 'Start your foundational routine with the AM/PM regimen above. Focus on consistency for 2 weeks before adding treatments.';
  }
  
  if (concerns.some(c => c.includes('dehydrated') || c.includes('dry'))) {
    return 'Begin the 14-day hydration challenge: Use the Baobab Intense Hydration Mask twice weekly and track your skin\'s moisture levels.';
  }
  
  if (concerns.some(c => c.includes('acne'))) {
    return 'Start with the 30-day clarity protocol: Use the Honeybush Clarifying Treatment 3x weekly, gradually increasing as tolerated.';
  }
  
  if (concerns.some(c => c.includes('aging'))) {
    return 'Begin your age-defying journey: Focus on consistent SPF use and introduce antioxidant-rich products gradually.';
  }
  
  if (concerns.some(c => c.includes('sensitive'))) {
    return 'Start the 21-day barrier repair program: Use only the gentle cleanser, toner, and moisturizer for 3 weeks before adding treatments.';
  }
  
  return 'Continue with your personalized regimen. Schedule a follow-up analysis in 4-6 weeks to track progress.';
}

/**
 * Main analysis function
 */
export function analyzeSkin(
  userProfile: Partial<UserProfile>,
  questionnaire: Record<string, any>,
  imageMetadata?: Record<string, any>
): SkinReport {
  // Detect climate context
  const climateContext = detectClimateContext(userProfile.environment || '');
  
  // Select hero botanical
  const heroBotanical = selectHeroBotanical(
    userProfile.skinType || questionnaire.skinType || 'combination',
    questionnaire.concerns || [],
    userProfile.environment || ''
  );
  
  // Generate findings
  const findings = generateFindings(userProfile, questionnaire, climateContext);
  
  // Build regimen plan
  const regimenPlan = buildRegimenPlan(
    userProfile.skinType || questionnaire.skinType || 'combination',
    questionnaire.concerns || [],
    heroBotanical.key,
    questionnaire.sensitivity || 'low'
  );
  
  // Generate cautions
  const cautions = generateCautions(
    regimenPlan,
    questionnaire.sensitivity || 'low',
    questionnaire.concerns || []
  );
  
  // Determine next action
  const nextBestAction = determineNextBestAction(
    findings,
    questionnaire.concerns || [],
    questionnaire.currentRoutine || 'minimal'
  );
  
  return {
    findings,
    regimenPlan,
    cautions,
    heroBotanical: heroBotanical.info.name,
    heroBotanicalOrigin: heroBotanical.info.origin,
    heroBotanicalBenefits: heroBotanical.info.benefits,
    nextBestAction,
    skinProfile: {
      type: userProfile.skinType || questionnaire.skinType || 'combination',
      concerns: questionnaire.concerns || [],
      sensitivity: questionnaire.sensitivity || 'low',
      environment: userProfile.environment || 'Not specified'
    },
    climateContext: climateContext.name
  };
}

/**
 * Export report as JSON
 */
export function exportReportAsJSON(report: SkinReport): string {
  return JSON.stringify(report, null, 2);
}

/**
 * Generate human-readable report summary
 */
export function generateReportSummary(report: SkinReport): string {
  const lines: string[] = [];
  
  lines.push('# Your Personalized Skin Analysis Report');
  lines.push('');
  lines.push(`## Climate Context: ${report.climateContext}`);
  lines.push('');
  lines.push('## Key Findings');
  report.findings.forEach(finding => {
    lines.push(`- **${finding.label}**: ${finding.value} (${Math.round(finding.confidence * 100)}% confidence)`);
    lines.push(`  ${finding.explanation}`);
    lines.push('');
  });
  
  lines.push('## Your AM Routine');
  report.regimenPlan.am.forEach(step => {
    lines.push(`${step.step}. **${step.category}**: ${step.productName}`);
    lines.push(`   ${step.instruction}`);
    lines.push('');
  });
  
  lines.push('## Your PM Routine');
  report.regimenPlan.pm.forEach(step => {
    lines.push(`${step.step}. **${step.category}**: ${step.productName}`);
    lines.push(`   ${step.instruction}`);
    lines.push('');
  });
  
  if (report.regimenPlan.weekly) {
    lines.push('## Weekly Treatments');
    report.regimenPlan.weekly.forEach(step => {
      lines.push(`- **${step.productName}**: ${step.instruction}`);
      lines.push('');
    });
  }
  
  lines.push('## Featured African Botanical');
  lines.push(`**${report.heroBotanical}** — ${report.heroBotanicalOrigin}`);
  lines.push('');
  lines.push('Key Benefits:');
  report.heroBotanicalBenefits.forEach(benefit => {
    lines.push(`- ${benefit}`);
  });
  lines.push('');
  
  lines.push('## Important Cautions');
  report.cautions.forEach(caution => {
    lines.push(`⚠️ ${caution}`);
  });
  lines.push('');
  
  lines.push('## Next Steps');
  lines.push(report.nextBestAction);
  lines.push('');
  
  lines.push('---');
  lines.push('*This analysis is generated by AI and should be used as guidance. Consult a dermatologist for persistent skin concerns.*');
  
  return lines.join('\n');
}

export { HERO_BOTANICALS, SA_CLIMATE_CONTEXTS, PRODUCT_CATALOG };

export default {
  analyzeSkin,
  exportReportAsJSON,
  generateReportSummary,
  HERO_BOTANICALS,
  SA_CLIMATE_CONTEXTS,
  PRODUCT_CATALOG
};
